import { alpha } from '@mui/material';

const styles = {
  '@keyframes pulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.7 }
  },

  container: {
    padding: 3
  },

  headerBox: {
    p: 2.5,
    mb: 3,
    borderRadius: 3,
    background: `linear-gradient(135deg, ${alpha('#ffffff', 0.9)} 0%, ${alpha('#f8f9fa', 0.9)} 100%)`,
    backdropFilter: 'blur(10px)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    border: `1px solid ${alpha('#000000', 0.05)}`
  },

  searchContainer: {
    width: { xs: '100%', sm: '350px' }
  },

  searchInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2.5,
      backgroundColor: '#ffffff',
      transition: 'all 0.3s ease',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
      '&:hover': {
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        transform: 'translateY(-1px)'
      },
      '&.Mui-focused': {
        boxShadow: '0 4px 16px rgba(59, 130, 246, 0.25)',
        borderColor: '#3b82f6'
      }
    }
  },

  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
    gap: 2
  },
  loadingText: {
    animation: 'pulse 1.5s ease-in-out infinite',
    fontWeight: 500
  },

  gridContainer: {
    mt: 0
  },

  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 4,
    overflow: 'hidden',
    border: `1px solid ${alpha('#000000', 0.06)}`,
    background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      boxShadow: '0 12px 32px rgba(0, 0, 0, 0.12)',
      transform: 'translateY(-6px)',
      borderColor: alpha('#3b82f6', 0.4)
    }
  },

  cardHeader: {
    background: `linear-gradient(135deg, ${alpha('#f8fafc', 0.8)} 0%, ${alpha('#e8f0fe', 0.3)} 100%)`,
    p: 2.5,
    borderBottom: `2px solid ${alpha('#3b82f6', 0.1)}`
  },

  cardHeaderContent: {
    display: 'flex',
    alignItems: 'center'
  },

  userAvatar: {
    bgcolor: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    mr: 2,
    width: 48,
    height: 48,
    fontSize: '1.3rem',
    fontWeight: 700,
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
    border: `2px solid ${alpha('#ffffff', 0.8)}`
  },

  userName: {
    fontWeight: 700,
    fontSize: '1.1rem',
    color: '#1e293b',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    letterSpacing: '-0.02em'
  },

  cardContent: {
    flexGrow: 1,
    p: 2.5,
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5
  },

  infoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    p: 1.2,
    borderRadius: 2,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: alpha('#3b82f6', 0.05),
      transform: 'translateX(-2px)'
    }
  },

  infoIcon: {
    color: '#64748b',
    fontSize: '1.3rem',
    flexShrink: 0,
    transition: 'color 0.2s ease'
  },

  infoText: {
    color: '#475569',
    fontSize: '0.875rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    fontWeight: 500
  },

  editIconButton: {
    ml: 'auto',
    color: 'primary.main',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: alpha('#3b82f6', 0.12),
      transform: 'rotate(15deg) scale(1.1)'
    }
  },

  roleChipContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    p: 1.2,
    borderRadius: 2,
    mt: 0.5
  },

  cardActions: {
    justifyContent: 'center',
    p: 2.5,
    pt: 0,
    pb: 2.5
  },

  editButton: {
    borderRadius: 2.5,
    textTransform: 'none',
    fontWeight: 600,
    py: 1.2,
    fontSize: '0.9rem',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
      boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)',
      transform: 'translateY(-2px)'
    },
    '&:active': {
      transform: 'translateY(0)'
    }
  },

  roleChip: (role) => {
    const colors = {
      approver: {
        bg: alpha('#3b82f6', 0.15),
        color: '#1d4ed8',
        border: alpha('#3b82f6', 0.4),
        hoverBg: '#3b82f6',
        hoverColor: '#ffffff'
      },
      accountManager: {
        bg: alpha('#9c27b0', 0.15),
        color: '#7b1fa2',
        border: alpha('#9c27b0', 0.4),
        hoverBg: '#9c27b0',
        hoverColor: '#ffffff'
      },
      regular: {
        bg: alpha('#64748b', 0.15),
        color: '#475569',
        border: alpha('#64748b', 0.4),
        hoverBg: '#64748b',
        hoverColor: '#ffffff'
      }
    };

    const colorScheme = colors[role] || colors.regular;

    return {
      backgroundColor: colorScheme.bg,
      color: colorScheme.color,
      border: `1.5px solid ${colorScheme.border}`,
      fontWeight: 700,
      fontSize: '0.8rem',
      px: 1.5,
      py: 0.8,
      letterSpacing: '0.02em',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        backgroundColor: colorScheme.hoverBg,
        color: colorScheme.hoverColor,
        transform: 'scale(1.08)',
        boxShadow: `0 4px 12px ${alpha(colorScheme.hoverBg, 0.3)}`
      }
    };
  },

  emptyBox: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    py: 10
  },

  emptyIcon: {
    fontSize: '5rem',
    color: '#cbd5e1',
    mb: 3,
    opacity: 0.7
  },

  emptyTitle: {
    fontWeight: 600,
    color: '#64748b',
    mb: 1,
    fontSize: '1.2rem'
  },

  emptySubtitle: {
    fontSize: '0.95rem',
    color: '#94a3b8'
  },

  dialog: {
    borderRadius: '20px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
    overflow: 'hidden'
  },

  dialogTitle: {
    background: `linear-gradient(135deg, ${alpha('#ffffff', 0.95)} 0%, ${alpha('#f8f9fa', 0.95)} 100%)`,
    p: 3,
    pb: 2.5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `2px solid ${alpha('#3b82f6', 0.1)}`
  },

  dialogTitleText: {
    fontSize: '1.3rem',
    fontWeight: 700,
    color: '#1e293b',
    letterSpacing: '-0.02em'
  },

  dialogCloseButton: {
    color: '#9e9e9e',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: alpha('#f44336', 0.12),
      color: '#f44336',
      transform: 'rotate(90deg) scale(1.1)'
    }
  },

  dialogContent: {
    p: 3,
    pt: 2.5,
    minWidth: { xs: '280px', sm: '400px' }
  },

  userInfoBox: {
    mb: 3,
    mt: 1,
    p: 2.5,
    borderRadius: 3,
    background: `linear-gradient(135deg, ${alpha('#f8fafc', 0.9)} 0%, ${alpha('#e8f0fe', 0.5)} 100%)`,
    border: `1px solid ${alpha('#3b82f6', 0.15)}`,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
  },

  userInfoRow: {
    display: 'flex',
    gap: 1.5,
    mb: 1.5,
    '&:last-child': {
      mb: 0
    }
  },

  userInfoLabel: {
    fontWeight: 700,
    color: '#64748b',
    minWidth: '80px',
    fontSize: '0.95rem'
  },

  userInfoValue: {
    color: '#1e293b',
    fontWeight: 500,
    fontSize: '0.95rem'
  },

  dialogForm: {
    mt: 2,
    '& .MuiOutlinedInput-root': {
      borderRadius: 2.5,
      transition: 'all 0.2s ease',
      '&:hover': {
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      },
      '&.Mui-focused': {
        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)'
      }
    }
  },

  dialogActions: {
    p: 3,
    gap: 2,
    borderTop: `2px solid ${alpha('#000000', 0.06)}`,
    background: `linear-gradient(to top, ${alpha('#f8f9fa', 0.5)} 0%, transparent 100%)`,
    display: 'flex',
    flexDirection: 'row-reverse',
    justifyContent: 'flex-start'
  },

  cancelButton: {
    borderRadius: 2.5,
    textTransform: 'none',
    fontWeight: 600,
    px: 3,
    py: 1,
    color: '#64748b',
    border: `1.5px solid ${alpha('#000000', 0.15)}`,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: alpha('#000000', 0.05),
      borderColor: alpha('#000000', 0.25),
      transform: 'translateY(-1px)'
    }
  },

  submitButton: {
    borderRadius: 2.5,
    textTransform: 'none',
    fontWeight: 700,
    px: 3,
    py: 1,
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
      boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)',
      transform: 'translateY(-2px)'
    },
    '&:active': {
      transform: 'translateY(0)'
    }
  }
};

export default styles;